import React, {Component} from "react";

import * as Pages from "./LocationForm";

import * as PROPS from "../static_props/MyTree";

import {
	fromLonLat,
	toLonLat,
} from "ol/proj";
import VectorSource from "ol/source/Vector";
import Point from "ol/geom/Point";
import {Style, Icon} from "ol/style";
import Feature from "ol/Feature";
import {Vector} from "ol/layer";

import "../../stylesheets/location-selection.scss";

import tree from "../../img/itree_treeonly_transparent_w_stroke.png";
import {
	extractAddressComponents,
	getAddressFromLatLng,
	getLatLngFromAddress,
} from "../../utilities/utilities";
import {DEVMODE} from "../../utilities/resources";


export class LocationWrapper extends Component {
	constructor(props) {
		super(props);

		this.state = {
			warning: "",
		};

		let iconParameters = {
			anchor: [0.5, 30],
			anchorXUnits: "fraction",
			anchorYUnits: "pixels",
			src: tree,
		};

		this.feature = new Feature({
			geometry: new Point(fromLonLat(this.props.location.getCenter())),
		});

		this.layers = [
			new Vector({
				source: new VectorSource({
					features: [this.feature],
				}),
				style: new Style({
					image: new Icon(iconParameters),
				}),
			})
		];
	}

	onMapClick = async (event) => {
		let coordinate = toLonLat(event.coordinate);
		let position = {
			latitude: coordinate[1],
			longitude: coordinate[0]
		};

		let response = await getAddressFromLatLng(position);
		let components;
		if (response.status.toLowerCase() === "ok") {
			let result = response.results[0];
			components = extractAddressComponents(result);
		} else if (response.status.includes("REQUEST_DENIED")) {
			let response = await this.handleGeolocationViaEngine(position);
			if (response.status.toLowerCase() === "ok") {
				let {
					city = "",
					county = "",
					state = "",
					nation = "",
				} = response.data.location;
				let stateAbbr = response.data.location["state-abbr"];
				components = {
					address: `${city || county}, ${state}, ${nation}`,
					nation: nation,
					stateAbbr: stateAbbr,
					county: {
						long: county,
					},
					city: {
						long: city || county,
					},
				};
			} else {
				this.handleBadResponse(response);
			}
		} else {
			this.handleBadResponse(response);
		}
		if (components.address) {
			this.handleLocationUpdate(
				components.address,
				components,
				position.latitude,
				position.longitude
			);
		} else {
			alert("Unable to locate address");
		}
	};

	componentDidUpdate(prevProps, prevState, snapshot) {
		this.feature.setGeometry(
			new Point(
				fromLonLat(this.props.location.getCenter()
				)
			)
		);
	}

	handleBadResponse = (response) => {
		let warning = "";

		if (
			response.status.includes("ZERO_RESULTS") ||
			response.status.includes("INVALID_REQUEST")
		) {
			warning = "No results were returned or this" +
				" address does not exist. Please try a" +
				" different address.";
		} else if (
			response.status.includes("OVER_DAILY_LIMIT") ||
			response.status.includes("OVER_QUERY_LIMIT") ||
			response.status.includes("REQUEST_DENIED")
		) {
			warning = "The geocoding service is" +
				" currently unavailable.";
		} else if (
			response.status.includes("TOO_BROAD")
		) {
			warning = "This address is too vague. Please try" +
				" one that is more specific.";
		} else {
			warning = "There has been an error while " +
				"geocoding this address. Please try again.";
		}

		this.setState({
			warning: warning
		});
	};
	handleGeolocationViaEngine = async (position) => {
		let controller = new AbortController();
		let signal = controller.signal;
		let time = 1000 * 60 * 30; // 30 minutes
		let timeout = setTimeout(() => controller.abort("Server timed out"), time);

		signal.addEventListener("abort", () => {
			clearTimeout(timeout);
			controller = null;
		});

		let formData = new FormData();
		formData.set("longitude", position.longitude);
		formData.set("latitude", position.latitude);
		formData.set("key", process.env.REACT_APP_ITREE_ENGINE_API);

		let url = `https://${DEVMODE ? "dev." : ""}engine.itreetools.org/v3/location`;
		let request = new Request(
			url,
			{
				body: formData,
				method: "POST",
				signal,
			}
		);
		let response = {
			status: "",
			data: [],
			message: "",
		};
		let result;
		try {
			result = await fetch(request);
		} catch (error) {
			response.status = "error";
			response.message = error;
		}

		if (result.ok) {
			response = await result.json();
		} else {
			response.status = "error";
			response.message = result.message;
		}
		if (controller) controller.abort();
		return response;
	};

	handleGeolocation = async (position) => {
		document.body.style.cursor = "";
		let addressFromLatLng = await getAddressFromLatLng(position);

		if (addressFromLatLng.status.toLowerCase() === "ok") {
			let result = addressFromLatLng.results[0];
			let latitude = result.geometry.location.lat();
			let longitude = result.geometry.location.lng();
			let components = extractAddressComponents(result);
			this.handleLocationUpdate(
				components.address,
				components,
				latitude,
				longitude
			);
		} else if (addressFromLatLng.status.includes("REQUEST_DENIED")) {
			let response = await this.handleGeolocationViaEngine(position);
			if (response.status.toLowerCase() === "ok") {
				let {
					city = "",
					county = "",
					state = "",
					latitude = "",
					longitude = "",
					nation = "",
				} = response.data.location;
				let stateAbbr = response.data.location["state-abbr"];
				let components = {
					address: `${city || county}, ${state}, ${nation}`,
					nation: nation,
					stateAbbr: stateAbbr,
					county: {
						long: county,
					},
					city: {
						long: city || county,
					},
				};
				this.handleLocationUpdate(
					components.address,
					components,
					latitude,
					longitude
				);
			} else {
				this.handleBadResponse(response);
			}
		} else {
			this.handleBadResponse(addressFromLatLng);
		}
	};

	handleAddressSearch = async (address) => {
		document.body.style.cursor = "";
		let latLngFromAddress = await getLatLngFromAddress(address);
		if (latLngFromAddress.status.toLowerCase() === "ok") {
			let result = latLngFromAddress.results[0];
			let components = extractAddressComponents(result);
			let latitude = result.geometry.location.lat();
			let longitude = result.geometry.location.lng();
			this.handleLocationUpdate(
				address,
				components,
				latitude,
				longitude
			);
		} else {
			this.setState(
				{
					warning: "Address search is currently unavailable. Please" +
						" try clicking on your desired location on the map."
				}
			);
		}
	};

	handleLocationUpdate = (
		address,
		components,
		latitude,
		longitude,
		callback
	) => {
		this.props.updateProject({
				location: {
					address: {
						entered: address,
						formatted: components.address,
					},
					latitude: latitude,
					longitude: longitude,
					nation: components.nation,
					stateAbbr: components.stateAbbr,
					county: components.county.long,
					city: components.city.long,
				},
			},
			callback ? callback() : null
		);
	};

	render() {
		let mapParameters = {
			imagerySets: [
				{name: "RoadOnDemand", visible: false},
				{name: "AerialWithLabelsOnDemand", visible: true},
			],
			layers: this.layers,
			activeImagery: 1,
		};

		return (
			<Pages.LocationForm
				{...this.props}
				{...PROPS.LOCATION}
				{...mapParameters}
				location={this.props.location}
				infoSubject={"location"}
				warning={this.state.warning}
				onMapClick={this.onMapClick}
				handleGeolocation={this.handleGeolocation}
				handleAddressSearch={this.handleAddressSearch}
				handleLocationUpdate={this.handleLocationUpdate}
				updateProject={this.props.updateProject}
			/>
		)
	}
}
